import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ChatMessage {
  id: string;
  listId: string;
  text: string;
  createdAt: string;
  readCount?: number;
  user: { id: string; name: string };
}

interface Props {
  listId: string;
  socket: any;
  onUnreadChange?: (count: number) => void;
}

export default function ChatDrawer({ listId, socket, onUnreadChange }: Props) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef(open);
  openRef.current = open;

  // Load history when drawer opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // Mark as read
    api.post(`/lists/${listId}/chat/read`).catch(console.error);
    api.get(`/lists/${listId}/chat`)
      .then(res => setMessages(res.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, listId]);

  // Scroll to bottom when messages change (only when open)
  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setUnread(0);
      onUnreadChange?.(0);
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open]);

  // Socket listener
  useEffect(() => {
    if (!socket) return;
    const handler = (msg: ChatMessage) => {
      if (msg.listId !== listId) return;
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      if (openRef.current) {
        // Drawer is open → mark as read immediately
        api.post(`/lists/${listId}/chat/read`).catch(console.error);
      } else {
        setUnread(prev => {
          const next = prev + 1;
          onUnreadChange?.(next);
          return next;
        });
      }
    };
    socket.on('chat:message', handler);
    return () => socket.off('chat:message', handler);
  }, [socket, listId]);

  const send = () => {
    const text = input.trim();
    if (!text || !socket) return;
    socket.emit('chat:send', { listId, text });
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) + ' ' +
      d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Chat öffnen"
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '0.5rem',
          color: open ? 'var(--primary)' : 'var(--text-secondary)',
          fontSize: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '44px',
          minHeight: '44px',
          transition: 'color 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute',
            top: 2,
            right: 2,
            background: '#ef4444',
            color: 'white',
            fontSize: '0.65rem',
            fontWeight: 'bold',
            borderRadius: '9999px',
            minWidth: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 3px',
            lineHeight: 1,
          }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            right: 'unset',
            width: '100%',
            maxWidth: '680px',
            zIndex: 500,
            background: 'var(--bg-primary)',
            borderTop: '1px solid var(--border-color)',
            borderRadius: '1rem 1rem 0 0',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column',
            height: '55vh',
            maxHeight: '520px',
          }}
        >
          {/* Drawer Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.875rem 1rem 0.75rem',
            borderBottom: '1px solid var(--border-color)',
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: '600', fontSize: '1rem', color: 'var(--text-primary)' }}>
              💬 Chat
            </span>
            <button
              onClick={() => setOpen(false)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', fontSize: '1.5rem',
                lineHeight: 1, padding: '0.25rem',
              }}
              aria-label="Chat schließen"
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0.75rem 1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.625rem',
          }}>
            {loading && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem', textAlign: 'center', marginTop: '2rem' }}>
                Lädt…
              </div>
            )}
            {!loading && messages.length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem', textAlign: 'center', marginTop: '2rem' }}>
                Noch keine Nachrichten. Schreib etwas!
              </div>
            )}
            {messages.map(msg => {
              const isOwn = msg.user.id === user?.id;
              return (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isOwn ? 'flex-end' : 'flex-start',
                  }}
                >
                  {!isOwn && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginBottom: '0.2rem', paddingLeft: '0.25rem' }}>
                      {msg.user.name}
                    </span>
                  )}
                  <div style={{
                    maxWidth: '75%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: isOwn ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                    background: isOwn ? 'var(--primary)' : 'var(--bg-tertiary)',
                    color: isOwn ? '#0d1117' : 'var(--text-primary)',
                    fontSize: '0.9375rem',
                    lineHeight: '1.4',
                    wordBreak: 'break-word',
                  }}>
                    {msg.text}
                  </div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: '0.2rem', paddingRight: isOwn ? '0.25rem' : 0, paddingLeft: isOwn ? 0 : '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {formatTime(msg.createdAt)}
                    {isOwn && typeof msg.readCount === 'number' && (
                      <span title={`${msg.readCount} gelesen`} style={{ opacity: msg.readCount > 1 ? 1 : 0.4 }}>
                        {msg.readCount > 1 ? '✓✓' : '✓'}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '0.75rem 1rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            gap: '0.5rem',
            flexShrink: 0,
            paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
          }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nachricht schreiben…"
              className="input"
              style={{ flex: 1, marginBottom: 0 }}
            />
            <button
              onClick={send}
              disabled={!input.trim()}
              className="btn btn-primary"
              style={{ flexShrink: 0, padding: '0.5rem 1rem' }}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
